import React, { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { ExternalLink, Search, Sparkles, Clock, User } from 'lucide-react';
import PushButton from '../components/PushButton';
import hubdevResources from '../data/hubdev_resources.json';
import manualResources from '../data/manual_resources.json';
import { SignedIn, SignedOut, SignInButton, UserButton, useAuth } from '@clerk/clerk-react';
const HUB_SOURCES = [];

// Canonical categories and icons in the order you provided..
const CANONICAL_CATEGORIES = [
  { key: 'Discover', icon: '🔎' },
  { key: 'Categories', icon: '📁' },
  { key: 'AI', icon: '🤖' },
  { key: 'APIs', icon: '⚙️' },
  { key: 'Accessibility', icon: '👀' },
  { key: 'Analytics', icon: '📈' },
  { key: 'Animation', icon: '🎞️' },
  { key: 'Authentication', icon: '🔐' },
  { key: 'CMS', icon: '🗂️' },
  { key: 'Challenges', icon: '🧠' },
  { key: 'Charts', icon: '📊' },
  { key: 'Course', icon: '🎓' },
  { key: 'Database', icon: '💾' },
  { key: 'Documentation', icon: '📖' },
  { key: 'Domain', icon: '🔗' },
  { key: 'Email', icon: '📧' },
  { key: 'Extensions', icon: '💫' },
  { key: 'Font', icon: '🔠' },
  { key: 'Forms', icon: '📝' },
  { key: 'Frameworks', icon: '🪄' },
  { key: 'Hosting', icon: '📡' },
  { key: 'Icon', icon: '🖼️' },
  { key: 'Illustration', icon: '🎨' },
  { key: 'Inspiration', icon: '💡' },
  { key: 'Newsletter', icon: '📰' },
  { key: 'Performance', icon: '🚀' },
  { key: 'Productivity', icon: '⚡' },
  { key: 'Reading', icon: '📚' },
  { key: 'Storage', icon: '💾' },
  { key: 'Testing', icon: '🧪' },
  { key: 'Toast', icon: '🔔' },
  { key: 'Tool', icon: '🛠️' },
  { key: 'UI', icon: '🧩' }
];

function normalizeCategoryString(s) {
  if (!s) return '';
  return s.toString().trim().toLowerCase();
}

function matchesCategory(itemCategory, canonicalKey) {
  if (!itemCategory || !canonicalKey) return false;
  const item = normalizeCategoryString(itemCategory);
  const canonical = normalizeCategoryString(canonicalKey);
  
  // Exact match first
  if (item === canonical) return true;
  
  // Handle common variations
  const variations = {
    'apis': 'api',
    'ais': 'ai',
    'uis': 'ui'
  };
  
  const itemNorm = variations[item] || item;
  const canonicalNorm = variations[canonical] || canonical;
  
  return itemNorm === canonicalNorm;
}

// Normalize sources: ensure category strings are trimmed and consistent
const combinedSources = [
  ...HUB_SOURCES,
  ...(Array.isArray(manualResources) ? manualResources : []),
  ...(Array.isArray(hubdevResources) ? hubdevResources : [])
].map(item => ({
  ...item,
  category: typeof item.category === 'string' ? item.category.trim() : item.category
}));

// Derive categories from normalized combinedSources using canonical list.
const availableCanonical = CANONICAL_CATEGORIES.filter(c =>
  combinedSources.some(item => matchesCategory(item.category, c.key))
);

const categories = [
  'All',
  ...availableCanonical.map(c => c.key)
];

export default function Hub() {
  // keep a small compatibility with Home header
  const { isSignedIn } = useAuth();
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const headerRef = useRef(null);
  const [lastUpdate] = useState(new Date());

  const filtered = useMemo(() => {
    try {
      const q = query.trim().toLowerCase();
      return combinedSources.filter(s => {
        if (!s) return false;
        if (selectedCategory !== 'All') {
          // match by category OR by tags (some items use tags to categorise)
          const inCategory = matchesCategory(s.category, selectedCategory);
          const inTags = Array.isArray(s.tags) && s.tags.some(t => normalizeCategoryString(t) === normalizeCategoryString(selectedCategory));
          if (!inCategory && !inTags) return false;
        }
        if (!q) return true;
        const name = (s.name || '').toString().toLowerCase();
        const desc = (s.description || '').toString().toLowerCase();
        const tags = (s.tags || []).join(' ').toLowerCase();
        return (
          name.includes(q) ||
          desc.includes(q) ||
          tags.includes(q)
        );
      });
    } catch (err) {
      // Defensive: if unexpected data or an extension error causes an exception,
      // log and return an empty array so the UI doesn't crash.
      // Many reported console errors come from browser extensions (source: chrome-extension://...)
      // which can interfere with page scripts; this guard keeps the page usable.
      // eslint-disable-next-line no-console
      console.error('Error while filtering hub items', err);
      return [];
    }
  }, [query, selectedCategory]);

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header (matching Home) */}
      <header ref={headerRef} className="bg-white/90 backdrop-blur-lg border-b border-slate-200 sticky top-0 z-50 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="bg-gradient-to-br from-blue-600 to-indigo-600 p-2 rounded-xl shadow-lg">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-700 to-indigo-700 bg-clip-text text-transparent">
                  AI News Hub
                </h1>
                <p className="text-xs text-slate-600">Your daily dose of AI & Tech</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="hidden md:flex items-center gap-2 text-xs text-slate-500">
                <Clock className="w-4 h-4" />
                <span>Last update: {lastUpdate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}</span>
              </div>
              <div className="hidden md:flex items-center">
                <div className="mr-2">
                  <PushButton />
                </div>
                {/* Quick link to Hub (when viewing Hub it's highlighted via CSS) */}
                <div className="mr-3">
                  <Link to="/hub" className="px-3 py-2 rounded-md text-sm font-medium bg-white border border-slate-200 hover:bg-slate-50 text-slate-700">
                    Hub
                  </Link>
                </div>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    type="text"
                    placeholder="Search hub..."
                    value={searchQuery}
                    onChange={(e) => { setSearchQuery(e.target.value); setQuery(e.target.value); }}
                    className="pl-10 w-64 bg-white border-slate-200 focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3">
                {/* Small-screen Hub link (visible on mobile/tablet) */}
                <Link to="/hub" className="md:hidden px-2 py-1 rounded text-sm text-slate-700 hover:bg-slate-100 border border-slate-200">
                  Hub
                </Link>
                <SignedOut>
                  <SignInButton>
                    <button aria-label="Open login" className="p-2 rounded-full hover:bg-slate-100">
                      <User className="w-5 h-5 text-slate-700" />
                    </button>
                  </SignInButton>
                </SignedOut>
                {/* Fallback visible login link in case Clerk UI fails to render */}
                {!isSignedIn && (
                  <Link to="/suscription" className="hidden md:inline-block ml-2 px-3 py-1 rounded text-sm text-slate-700 bg-white border border-slate-200 hover:bg-slate-50">
                    Login
                  </Link>
                )}
                <SignedIn>
                  <UserButton />
                </SignedIn>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex gap-8">
          {/* Sidebar (visible on large screens) */}
          <aside className="hidden lg:block w-64">
            <div className="sticky top-24">
              <h4 className="text-sm font-semibold text-slate-700 mb-4">Categories</h4>
              <ul className="space-y-2">
                <li key="All">
                  <button
                    onClick={() => setSelectedCategory('All')}
                    className={`flex items-center gap-3 w-full text-left px-3 py-2 rounded-md ${selectedCategory === 'All' ? 'bg-indigo-600 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                  >
                    <span className="text-lg">🔎</span>
                    <span className="text-sm font-medium">All</span>
                  </button>
                </li>
                {availableCanonical.map(c => (
                  <li key={c.key}>
                    <button
                      onClick={() => setSelectedCategory(c.key)}
                      className={`flex items-center gap-3 w-full text-left px-3 py-2 rounded-md ${selectedCategory === c.key ? 'bg-indigo-600 text-white' : 'text-slate-700 hover:bg-slate-50'}`}
                    >
                      <span className="text-lg">{c.icon}</span>
                      <span className="text-sm font-medium">{c.key}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          </aside>

          <main className="flex-1">
            <div className="mb-8 flex items-center justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-800">Hub — Resources by category</h1>
            <p className="text-sm text-slate-500 mt-1">A curated collection of API catalogs, developer hubs and free asset sites.</p>
          </div>
          <div className="w-full max-w-md">
            <label className="sr-only">Search hub</label>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input value={query} onChange={(e) => setQuery(e.target.value)} className="pl-10" placeholder="Search resources, tags or descriptions..." />
            </div>
          </div>
            </div>

            <div className="mb-6 flex gap-2 flex-wrap lg:hidden">
              {['All', ...availableCanonical.map(c => c.key)].map(cat => (
                <button
                  key={cat}
                  onClick={() => setSelectedCategory(cat)}
                  className={`px-3 py-1 rounded-full text-sm font-medium ${selectedCategory === cat ? 'bg-indigo-600 text-white' : 'bg-white border border-slate-200 text-slate-700 hover:bg-slate-50'}`}
                >
                  {cat}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map((item, idx) => {
            // derive a favicon url using Google's favicon service (works for most sites)
            let favicon = '';
            try {
              const hostname = new URL(item.url).hostname;
              favicon = `https://s2.googleusercontent.com/s2/favicons?domain=${hostname}&sz=128`;
            } catch (e) {
              favicon = '';
            }

            const image = item.image || favicon || null;

            return (
              <div key={`${item.id}-${idx}`} className="group bg-white rounded-2xl shadow-md hover:shadow-2xl transition-all overflow-hidden border border-slate-100">
                {/* Imagen superior */}
                <div className="relative w-full h-48 bg-gradient-to-br from-slate-100 via-slate-50 to-slate-100 overflow-hidden">
                  {image ? (
                    // use object-cover so the image fills the container height without top/bottom gaps
                    // object-center keeps the crop centered; subtle scale on hover
                    <img src={image} alt={item.name} className="w-full h-full object-cover object-center group-hover:scale-105 transition-transform duration-300" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <div className="text-6xl font-bold text-slate-400 opacity-30">{(item.name||'?').charAt(0).toUpperCase()}</div>
                    </div>
                  )}
                  {/* Badge de categoría flotante */}
                  <div className="absolute top-3 right-3">
                    <Badge className="bg-white/90 text-slate-800 text-xs font-medium px-2 py-1 shadow-sm">
                      {item.category}
                    </Badge>
                  </div>
                </div>

                {/* Contenido */}
                <div className="p-5 flex flex-col h-auto">
                  {/* Título */}
                  <h3 className="text-xl font-bold text-slate-900 line-clamp-2 mb-2 min-h-[3.5rem]">
                    {item.name}
                  </h3>

                  {/* Descripción */}
                  <p className="text-sm text-slate-600 line-clamp-3 mb-4 flex-1">
                    {item.description}
                  </p>

                  {/* Tags */}
                  {item.tags && item.tags.length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {item.tags
                        .filter(tag => {
                          // avoid repeating the category as a tag (case-insensitive)
                          try {
                            return tag && tag.toString().trim().toLowerCase() !== (item.category||'').toString().trim().toLowerCase();
                          } catch (e) { return true; }
                        })
                        .slice(0, 4)
                        .map((tag, tIdx) => (
                          <span key={`${tag}-${idx}-${tIdx}`} className="text-xs bg-slate-50 text-slate-600 px-2 py-1 rounded-md">
                            {tag}
                          </span>
                        ))}
                    </div>
                  )}

                  {/* CTA Button */}
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noreferrer"
                    className="w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 bg-slate-900 hover:bg-slate-800 text-white rounded-lg font-medium transition-colors shadow-sm"
                  >
                    Visit Resource
                    <ExternalLink className="w-4 h-4" />
                  </a>
                </div>
              </div>
            );
          })}
        </div>

            {filtered.length === 0 && (
              <div className="mt-8 text-center text-slate-500">No resources found — try a different search or category.</div>
            )}
          </main>
        </div>
      </div>

      {/* Footer (copied from Home for consistent layer) */}
      <footer className="bg-slate-900 text-white mt-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="grid md:grid-cols-3 gap-8 mb-8">
            <div>
                <h4 className="font-bold text-lg mb-3">AI News Hub</h4>
              <p className="text-slate-400 text-sm">
                Your trusted source for AI, programming and technology news.
              </p>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-3">Resources</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <a href="https://intheworldofai.com/" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">In The World of AI</a>
                <a href="https://www.hubdev.tools/category/ai" target="_blank" rel="noopener noreferrer" className="block hover:text-white transition-colors">HubDev — AI</a>
              </div>
            </div>
            <div>
              <h4 className="font-bold text-lg mb-3">Categories</h4>
              <div className="space-y-2 text-sm text-slate-400">
                <p>AI News</p>
                <p>Programming</p>
                <p>Machine Learning</p>
                <p>Research Papers</p>
              </div>
            </div>
          </div>
          <div className="border-t border-slate-800 pt-8 text-center">
            <p className="text-slate-400 text-sm">
              © 2024 AI News Hub. Automated collection of AI and programming news.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}
